import React, {
  ChangeEvent,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  ErrorMessage,
  Field,
  Form,
  Formik,
  FormikErrors,
  FormikProps,
} from 'formik';
import * as Styled from './LoginPage.styles';
import * as actions from './actions';
import { selectors } from './reducer';
import {
  formatPhoneNumberToBeValid,
  setToLocalStorage,
} from '../../global/helpers';
import CustomCheckbox from '../../components/CustomCheckbox';
import CodeConfirmation from './CodeConfirmation';
import { INITIAL_AUTHORIZATION_MODAL_FORM_STATE } from './constants';
import Loader from '../../components/Loader';
import useTelegramLoginWidget from './hooks/useTelegramLoginWidget';
import { TelegramAuthData } from './types';
import { validationCodeErrorHandler } from './helpers';

type AuthMethod = 'sms' | 'whatsApp';

interface FormValues {
  phone: string;
  agreementAccepted: boolean;
  method: AuthMethod;
}

const LoginPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const loading = useSelector(selectors.loading);
  const whatsAppAuthNumber = useSelector(selectors.whatsAppAuthNumber);
  const userData = useSelector(selectors.userData);

  const [isConfirmationCodeSent, setIsConfirmationCodeSent] =
    useState<boolean>(false);
  // eslint-disable-next-line
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [authMethod, setAuthMethod] = useState<AuthMethod>('sms');
  const [retryCountdown, setRetryCountdown] = useState<number>(0);
  const wasSubmitted = useSelector(selectors.wasSubmitted);
  const errorMessage = useSelector(selectors.errorMessage);
  const errorCode = useSelector(selectors.errorCode);
  const error = useSelector(selectors.error);

  const telegramContainerRef = useRef<HTMLDivElement>(null);

  const onTelegramAuth = useCallback(
    (data: TelegramAuthData) => {
      dispatch(actions.telegramAuth.request(data));
    },
    [dispatch]
  );

  const { triggerAuth: triggerTelegramAuth } = useTelegramLoginWidget(
    telegramContainerRef,
    onTelegramAuth,
    process.env.REACT_APP_TELEGRAM_BOT_ID
  );

  const handlePhoneChange = (
    props: FormikProps<any>,
    event: ChangeEvent<HTMLInputElement>,
    inputName: string
  ) => {
    // @ts-ignore
    const phoneInputValue = event.target.value.replaceAll(/[\s-()]/g, '');
    if (!phoneInputValue.match(/^\+7\d{0,10}$/)) {
      return;
    }
    props.setErrors({
      ...props.errors,
      [inputName]: '',
    });
    props.setFieldValue(inputName, phoneInputValue);
    setPhoneNumber(phoneInputValue);
    if (retryCountdown === 0) {
      dispatch(actions.clearErrorMessage());
    }
  };

  const handleAgreementCheckboxChange = (
    props: FormikProps<any>,
    isChecked: boolean
  ) => {
    props.setFieldValue('agreementAccepted', isChecked);
    props.setErrors({
      ...props.errors,
      agreementAccepted: '',
    });
  };

  const validateForm = (values: {
    phone: string;
    agreementAccepted: boolean;
  }) => {
    const { phone } = values;
    const errors: FormikErrors<FormValues> = {};

    if (!phone) {
      errors.phone = t('LoginPage.PHONE_EMPTY_ERROR');
    }
    if (!formatPhoneNumberToBeValid(values.phone)) {
      errors.phone = t('LoginPage.PHONE_INVALID_ERROR');
    }
    if (!values.agreementAccepted) {
      errors.agreementAccepted = t('LoginPage.ERROR_CHECKBOX');
    }
    return errors;
  };

  const sendMessageHandler = () => {
    const message = encodeURIComponent(
      String(t('CodeConfirmation.WHATSAPP_CODE'))
    );
    const whatsappUrl = `https://api.whatsapp.com/send?phone=${whatsAppAuthNumber}&text=${message}`;

    window.open(whatsappUrl, '_blank');
  };

  const handleSubmitPhoneForm = async (values: {
    phone: string;
    agreementAccepted: boolean;
    method: string;
  }) => {
    const typedMethod = values.method as AuthMethod;
    setPhoneNumber(values.phone);
    setAuthMethod(typedMethod);
    dispatch(
      actions.getValidationCode.request({
        phone: values.phone,
        method: typedMethod,
      })
    );
  };

  useEffect(() => {
    if (!wasSubmitted) return;

    const success = !loading && !error && !errorMessage && phoneNumber;

    if (success) {
      setIsConfirmationCodeSent(true);
      if (authMethod === 'whatsApp') {
        sendMessageHandler();
      }
    }
  }, [wasSubmitted, loading, error, errorMessage, phoneNumber, authMethod]);

  useEffect(() => {
    if (!error || !errorMessage) return;

    const match = errorMessage.match(/wait for (\d+) minute/);
    if (match) {
      const minutes = parseInt(match[1], 10);
      setRetryCountdown(minutes * 60);
    }
  }, [error, errorMessage]);

  useEffect(() => {
    if (retryCountdown <= 0) return;

    const timer = setInterval(() => {
      setRetryCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [retryCountdown]);

  useEffect(() => {
    if (userData) {
      window.location.href = '/orders';
    }
  }, [userData]);

  const pageContent = !isConfirmationCodeSent ? (
    <Styled.LoginPagePhoneConfirmation>
      <div className="title">{t('LoginPage.TITLE')}</div>
      <Formik
        initialValues={INITIAL_AUTHORIZATION_MODAL_FORM_STATE}
        onSubmit={handleSubmitPhoneForm}
        validate={validateForm}
      >
        {(props: FormikProps<any>) => (
          <Form className="form-container">
            <div className="form-block">
              <label className="form-label" htmlFor="phone">
                {t('LoginPage.PHONE_LABEL')}
              </label>
              <Field
                id="phone"
                name="phone"
                type="phone"
                className={`input ${props.errors.phone && 'input-error'}`}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handlePhoneChange(props, e, 'phone')
                }
              />
              <ErrorMessage
                name="phone"
                render={(msg: string) => (
                  <div className="error-message-label">{msg}</div>
                )}
              />

              {!loading && errorMessage && (
                <div className="error-message-label">
                  {errorCode &&
                    validationCodeErrorHandler(errorMessage, errorCode)}
                </div>
              )}
            </div>

            <Styled.LoginAgreementContainer>
              <div className="checkbox-container">
                <div className="checkbox">
                  <CustomCheckbox
                    id="agreement"
                    onChange={(e) => handleAgreementCheckboxChange(props, e)}
                    checked={props.values.agreementAccepted}
                    errorChecked={Boolean(props.errors.agreementAccepted)}
                  />
                </div>
                <span className="accept">
                  {t('LoginPage.ACCEPT')}
                  <a
                    href="/user-agreement.htm"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span>{t('LoginPage.USER_AGREEMENT')}</span>
                  </a>{' '}
                  и
                  <a
                    href="/confidentiality.htm"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span>{t('LoginPage.USER_CONFIDENTIALITY')}</span>
                  </a>
                </span>
              </div>
              {Boolean(props.errors.agreementAccepted) && (
                <div className="checkbox-error-message">
                  {t('LoginPage.ERROR_CHECKBOX')}
                </div>
              )}
            </Styled.LoginAgreementContainer>

            <Styled.LoginPageButtonsContainer>
              <Styled.FormSubmitButton
                type="submit"
                disabled={retryCountdown > 0}
                style={
                  props.values.agreementAccepted &&
                  !props.errors.phone &&
                  retryCountdown === 0
                    ? {}
                    : { opacity: 0.5 }
                }
                onClick={() => {
                  props.setFieldValue('method', 'sms');
                }}
              >
                <img src="/icons/sms.svg" alt="sms" />
                {retryCountdown > 0
                  ? `${Math.floor(retryCountdown / 60)}:${String(
                      retryCountdown % 60
                    ).padStart(2, '0')}`
                  : t('LoginPage.AUTH_SMS')}
              </Styled.FormSubmitButton>

              <Styled.SecondaryButtonsRow>
                {whatsAppAuthNumber !== '77777777777' && (
                  <Styled.SecondaryButton
                    type="submit"
                    disabled={retryCountdown > 0}
                    style={
                      props.values.agreementAccepted &&
                      !props.errors.phone &&
                      retryCountdown === 0
                        ? {}
                        : { opacity: 0.5 }
                    }
                    onClick={() => {
                      props.setFieldValue('method', 'whatsApp');
                    }}
                  >
                    <img src="/icons/whatsapp.svg" alt="whatsapp" />
                    {retryCountdown > 0
                      ? `${Math.floor(retryCountdown / 60)}:${String(
                          retryCountdown % 60
                        ).padStart(2, '0')}`
                      : t('LoginPage.AUTH_WHATSAPP')}
                  </Styled.SecondaryButton>
                )}

                {process.env.REACT_APP_TELEGRAM_BOT_ID && (
                  <>
                    <Styled.SecondaryButton
                      type="button"
                      disabled={!props.values.agreementAccepted}
                      style={
                        props.values.agreementAccepted ? {} : { opacity: 0.5 }
                      }
                      onClick={() => {
                        if (!props.values.agreementAccepted) {
                          props.setTouched({ agreementAccepted: true });
                          props.validateForm();
                          return;
                        }
                        triggerTelegramAuth();
                      }}
                    >
                      <img src="/icons/telegram.svg" alt="telegram" />
                      {t('LoginPage.AUTH_TELEGRAM')}
                    </Styled.SecondaryButton>
                    <Styled.TelegramWidgetContainer
                      ref={telegramContainerRef}
                    />
                  </>
                )}
              </Styled.SecondaryButtonsRow>
            </Styled.LoginPageButtonsContainer>
          </Form>
        )}
      </Formik>
    </Styled.LoginPagePhoneConfirmation>
  ) : (
    <CodeConfirmation
      phoneNumber={phoneNumber}
      whatsappMethod={authMethod === 'whatsApp'}
      whatsAppAuthNumber={whatsAppAuthNumber}
      sendMessageHandler={sendMessageHandler}
    />
  );

  useEffect(() => {
    dispatch(actions.getWhatsappAuthNumber.request());
  }, []);

  return (
    <>
      <Styled.LoginPageContainer>
        <Styled.LoginPageContent>{pageContent}</Styled.LoginPageContent>
      </Styled.LoginPageContainer>
      {loading && <Loader />}
    </>
  );
};

export default LoginPage;
